import { FC, Fragment, useEffect, useState } from 'react';
import { Link, useActionData, useNavigation, useSubmit } from 'react-router-dom';

import { Alert, Button, Card, CardBody, Col, Container, FormFeedback, Input, Label, Row } from 'reactstrap';

import classnames from 'classnames';
import { useFormik } from 'formik';
import * as Yup from 'yup';

import { useLoadingStore } from '@hsp/react-component-library';
import { useDate } from '@hsp/react-hooks';
import { isDefined } from '@hsp/utilities';

import logo from 'assets/images/logo.svg';
import profile from 'assets/images/profile-img.png';
import { IForgotPassword } from 'types/user';

const ForgotPasswordPage: FC = () => {
  const [state] = useState<IForgotPassword>({
    email: '',
  });

  const actionData = useActionData() as Record<string, unknown>;
  const formik = useFormik<IForgotPassword>({
    enableReinitialize: true,
    initialValues: {
      email: state.email,
    },
    onSubmit: async (values) => submit({ ...values }, { method: 'post' }),
    validationSchema: Yup.object().shape({
      email: Yup.string().required('Please Enter Your Email'),
    }),
  });
  const isError = isDefined(actionData) && typeof actionData === 'object' && 'error' in actionData;
  const setLoading = useLoadingStore(({ setIsLoading }) => setIsLoading);
  const navigation = useNavigation();
  const { year } = useDate();
  const submit = useSubmit();

  useEffect(() => setLoading(navigation.state === 'submitting'), [navigation.state, setLoading]);

  return (
    <Fragment>
      <div className="home-btn d-none d-sm-block">
        <Link
          className="text-dark"
          to="/">
          <i className="bx bx-home h2" />
        </Link>
      </div>
      <div className="account-pages my-5 pt-sm-5">
        <Container>
          <Row className="justify-content-center">
            <Col
              lg={6}
              md={8}
              xl={5}>
              <Card className="overflow-hidden">
                <div className="bg-primary bg-soft">
                  <Row>
                    <Col className="col-7">
                      <div className="text-primary p-4">
                        <h5 className="text-primary">Welcome Back !</h5>
                        <p>Sign in to continue to Skote.</p>
                      </div>
                    </Col>
                    <Col className="col-5 align-self-end">
                      <img
                        alt=""
                        className="img-fluid"
                        src={profile}
                      />
                    </Col>
                  </Row>
                </div>
                <CardBody className="pt-0">
                  <div>
                    <Link to="/">
                      <div className="avatar-md profile-user-wid mb-4">
                        <span className="avatar-title rounded-circle bg-light">
                          <img
                            alt=""
                            className="rounded-circle"
                            height="34"
                            src={logo}
                          />
                        </span>
                      </div>
                    </Link>
                  </div>
                  <div className="p-2">
                    {isError ? <Alert color="danger">{(actionData.error as Error).message}</Alert> : null}
                    {typeof actionData === 'string' ? <Alert color="success">{actionData}</Alert> : null}
                    <form
                      className="form-horizontal"
                      onSubmit={formik.handleSubmit}>
                      <div className="mb-3">
                        <Label
                          className="form-label"
                          for="email">
                          Email
                        </Label>
                        <Input
                          autoComplete="off"
                          className={classnames('form-control', {
                            'is-invalid': formik.errors.email && formik.touched.email,
                          })}
                          name="email"
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          type="email"
                          value={formik.values.email}
                        />
                        <FormFeedback type="invalid">{formik.errors.email}</FormFeedback>
                      </div>
                      <div className="text-end">
                        <Button
                          className="w-md"
                          color="primary"
                          disabled={navigation.state === 'submitting'}
                          type="submit">
                          Reset
                        </Button>
                      </div>
                    </form>
                  </div>
                </CardBody>
              </Card>
              <div className="mt-5 text-center">
                <p>
                  <Link
                    className="fw-medium text-primary"
                    to="/login">
                    Login
                  </Link>
                </p>
                <p>
                  ©{year} Skote. Crafted with <i className="mdi mdi-heart text-danger" /> by Themesbrand
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </Fragment>
  );
};

export default ForgotPasswordPage;
